import { base44 } from '@/api/base44Client';
import {
  getDanceTeamMemberByUser,
  getKidsTeamMemberByUser,
  getMediaTeamMemberByUser,
  getReceptionTeamMemberByUser,
  getWorshipTeamMemberByUser,
  isSameTeamMember
} from '@/lib/teamMemberLookup';

const TEAMS = [
  {
    key: 'worship',
    queryKey: 'availabilityBlocks',
    memberField: 'worship_member_id',
    memberNameField: 'worship_member_name',
    blockEntity: 'AvailabilityBlock',
    getMemberByUser: getWorshipTeamMemberByUser
  },
  {
    key: 'media',
    queryKey: 'mediaAvailabilityBlocks',
    memberField: 'media_member_id',
    memberNameField: 'media_member_name',
    blockEntity: 'MediaAvailabilityBlock',
    getMemberByUser: getMediaTeamMemberByUser
  },
  {
    key: 'dance',
    queryKey: 'danceAvailabilityBlocks',
    memberField: 'dance_member_id',
    memberNameField: 'dance_member_name',
    blockEntity: 'DanceAvailabilityBlock',
    getMemberByUser: getDanceTeamMemberByUser
  },
  {
    key: 'reception',
    queryKey: 'receptionAvailabilityBlocks',
    memberField: 'reception_member_id',
    memberNameField: 'reception_member_name',
    blockEntity: 'ReceptionAvailabilityBlock',
    getMemberByUser: getReceptionTeamMemberByUser
  },
  {
    key: 'kids',
    queryKey: 'kidsAvailabilityBlocks',
    memberField: 'kids_member_id',
    memberNameField: 'kids_member_name',
    blockEntity: 'KidsMBVAvailabilityBlock',
    getMemberByUser: getKidsTeamMemberByUser
  }
];

const SCHEDULES = [
  { entity: 'WorshipSchedule', label: 'Louvor' },
  { entity: 'MediaSchedule', label: 'Mídia' },
  { entity: 'DanceSchedule', label: 'Dança' },
  { entity: 'ReceptionSchedule', label: 'Recepção' },
  { entity: 'KidsMBVSchedule', label: 'Kids MBV' }
];

const normalizeName = (member, user) => member?.full_name || member?.name || user?.full_name || user?.email || 'Integrante';

async function getUserMemberships(user) {
  const resolved = await Promise.all(
    TEAMS.map(async (team) => {
      const member = await team.getMemberByUser(user);
      return member ? { team, member } : null;
    })
  );

  return resolved.filter(Boolean);
}

async function findExistingAssignment(user, memberships, dates) {
  const selectedDates = new Set(dates);
  const identities = [user, ...memberships.map(({ member }) => member)];
  let schedulesByTeam;
  try {
    schedulesByTeam = await Promise.all(
      SCHEDULES.map(async (team) => ({
        ...team,
        schedules: await base44.entities[team.entity].list('-date', 2000)
      }))
    );
  } catch {
    throw new Error('Não foi possível verificar suas escalas. Nenhum bloqueio foi salvo; tente novamente.');
  }

  for (const team of schedulesByTeam) {
    for (const schedule of team.schedules) {
      const date = String(schedule.date || '').slice(0, 10);
      if (!selectedDates.has(date)) continue;
      if (!(schedule.members || []).some((scaledMember) =>
        identities.some((identity) => isSameTeamMember(scaledMember, identity))
      )) continue;
      return { date, title: schedule.title || 'Escala', team: team.label };
    }
  }

  return null;
}

function formatBlockedDate(date) {
  const [year, month, day] = String(date).split('-');
  return `${day}/${month}/${year}`;
}

export async function createSyncedAvailabilityBlocks({ user, dates = [], reason }) {
  const normalizedDates = Array.from(new Set((dates || []).map((date) => String(date || '').slice(0, 10)).filter(Boolean)));
  if (!user || normalizedDates.length === 0) return { createdCount: 0 };

  const memberships = await getUserMemberships(user);
  if (memberships.length === 0) return { createdCount: 0 };

  const assignment = await findExistingAssignment(user, memberships, normalizedDates);
  if (assignment) {
    throw new Error(
      `Não é possível bloquear ${formatBlockedDate(assignment.date)}: você já está na escala "${assignment.title}" (${assignment.team}). Abra a escala e use "Solicitar Alteração" para pedir a troca.`
    );
  }

  const teamResults = await Promise.all(memberships.map(async ({ team, member }) => {
    try {
      const existingBlocks = await base44.entities[team.blockEntity].filter({ [team.memberField]: member.id }, 2000);
      const existingDateSet = new Set(existingBlocks.map((block) => String(block.blocked_date || '').slice(0, 10)));
      const datesToCreate = normalizedDates.filter((date) => !existingDateSet.has(date));

      if (datesToCreate.length === 0) return { createdCount: 0, failures: [] };

      const results = await Promise.allSettled(
        datesToCreate.map((blockedDate) =>
          base44.entities[team.blockEntity].create({
            [team.memberField]: member.id,
            [team.memberNameField]: normalizeName(member, user),
            blocked_date: blockedDate,
            reason: reason || undefined,
            created_by_email: user.email
          })
        )
      );

      return {
        createdCount: results.filter((result) => result.status === 'fulfilled').length,
        failures: results.filter((result) => result.status === 'rejected').map((result) => result.reason)
      };
    } catch (error) {
      return { createdCount: 0, failures: [error] };
    }
  }));

  const createdCount = teamResults.reduce((total, result) => total + result.createdCount, 0);
  const failures = teamResults.flatMap((result) => result.failures);

  if (createdCount === 0 && failures.length > 0) {
    throw failures[0];
  }

  return { createdCount };
}

export async function deleteSyncedAvailabilityBlock({ user, blockedDate }) {
  const normalizedDate = String(blockedDate || '').slice(0, 10);
  if (!user || !normalizedDate) return { deletedCount: 0 };

  const memberships = await getUserMemberships(user);
  if (memberships.length === 0) return { deletedCount: 0 };

  const deletedCounts = await Promise.all(memberships.map(async ({ team, member }) => {
    const blocksForDate = await base44.entities[team.blockEntity].filter(
      {
        [team.memberField]: member.id,
        blocked_date: normalizedDate
      },
      2000
    );

    if (!blocksForDate.length) return 0;

    await Promise.all(
      blocksForDate.map((block) => base44.entities[team.blockEntity].delete(block.id))
    );

    return blocksForDate.length;
  }));

  return { deletedCount: deletedCounts.reduce((total, count) => total + count, 0) };
}

export async function invalidateSyncedAvailabilityQueries(queryClient) {
  await Promise.all(
    TEAMS.map((team) => queryClient.invalidateQueries({ queryKey: [team.queryKey] }))
  );
}
